import React from 'react';
import { Navigate, Outlet } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';
import type { RrmsRole } from '../config/app.config';
import { buildPortalAccess, firstPortalPath } from '../config/permissions.config';

interface PermissionGuardProps {
  /** Dynamic-RBAC permission key (e.g. 'records.manage_content'). Super admin always passes. */
  permission: string;
  /** Legacy-role fallback for CMS/bypass sessions that carry no permission set. */
  fallbackRoles?: RrmsRole[];
  /** When true, pass only if the permission/session does NOT hold the permission. */
  absent?: boolean;
  /** Override redirect target when access is denied. */
  redirectTo?: string;
}

/**
 * Route guard driven by the admin master's RBAC: passes when the logged-in user
 * holds `permission` (granted via the Role Builder, any node scope) or is super
 * admin. `fallbackRoles` keeps legacy demo sessions working during migration.
 */
export const PermissionGuard: React.FC<PermissionGuardProps> = ({
  permission,
  fallbackRoles = [],
  absent = false,
  redirectTo,
}) => {
  const auth = useAuthStore();
  const { can } = auth;
  const holds = can(permission, fallbackRoles);
  const allowed = absent ? !holds : holds;

  if (!allowed) {
    const access = buildPortalAccess(auth);
    const target = redirectTo ?? firstPortalPath(access);
    return <Navigate to={target} replace />;
  }
  return <Outlet />;
};

export default PermissionGuard;
